import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { orderBy } from 'lodash-es';
import { filter, finalize, lastValueFrom, map, switchMap } from 'rxjs';

import {
  ContactAttemptDispositionType, ContactAttemptInfoType, CreateContactAttemptGQL,
  CreateContactAttemptInput,
  CustomFormDetailsFragment,
  EmailDetailsFragment, FieldStatus, GetEntityById2GQL, GetEntityById2Query, PhoneDetailsFragment
} from 'generated/graphql';

import {
  AddOrUpdateEmailDialog
} from 'entity/dialogs/add-or-update-email/add-or-update-email.dialog';
import {
  AddOrUpdatePhoneDialog
} from 'entity/dialogs/add-or-update-phone/add-or-update-phone.dialog';
import { IStatusable } from 'helpers';
import { GetCustomFormService } from 'app/custom-forms/GetCustomForm.service';
import { ToastrService } from 'core/toastr.service';

@Component({
  selector: 'app-create-contact-attempt',
  templateUrl: './create-contact-attempt.component.html',
  styleUrls: ['./create-contact-attempt.component.scss']
})

export class CreateContactAttemptComponent implements OnInit {
  loading = true;
  step = 1;
  saving = false;

  @Input() entityId: string;
  @Input() campaignId: string;
  @Input() initiallySelectedContactItem?: {id: string, __typename: string};

  // AB-813
  @Output() closeModal: EventEmitter<any> = new EventEmitter<any>();
  selectedContactResult: 'success' | 'attempted' | 'mark_as_bad' | 'do_not_contact' = 'success';

  note: string = null;
  customForm: CustomFormDetailsFragment = null;
  entity: GetEntityById2Query['getEntity'];

  selectedContactMethod: any = 'other';
  contactPhoneNumber: PhoneDetailsFragment;
  contactEmail: EmailDetailsFragment;

  constructor(
    private _cd: ChangeDetectorRef,
    private _getEntityByIdGQL: GetEntityById2GQL,
    private _createContactAttemptGQL: CreateContactAttemptGQL,
    private _getCustomFormSvc: GetCustomFormService,
    private _dialog: MatDialog,
    private _toastr: ToastrService
  ) {
  }

  async ngOnInit() {
    const { data } = await lastValueFrom(this.fetchData());
    const customForm = await this._getCustomFormSvc.getContactForm(data.getEntity.entityType.id, this.campaignId);

    if (!customForm) {
      this._toastr.error('No contact form found for this entity type in the campaign');
      return;
    }


    this.entity = data.getEntity;
    this.customForm = customForm;
    this.contactPhoneNumber = this.entity.bestPhoneNumber;
    this.contactEmail = this.entity.bestEmail;
    this.loading = false;

    if (this.initiallySelectedContactItem) {
      if (this.initiallySelectedContactItem.__typename === 'Email') {
        this.setContactEmail(this.entity.emails.find(e => e.id === this.initiallySelectedContactItem.id));
      }

      if (this.initiallySelectedContactItem.__typename === 'PhoneNumber') {
        this.setContactPhoneNumber(this.entity.phoneNumbers.find(p => p.id === this.initiallySelectedContactItem.id));
      }
    }

    this._cd.detectChanges();
  }

  private fetchData() {
    return this._getEntityByIdGQL.fetch({
      entityId: this.entityId,
      campaignId: this.campaignId,
      withAddresses: true,
      withEmails: true,
      withPhones: true,
      withTags: true,
      withFollowUpDetails: true,
      withToplines: true,
      withHighligtedTopline: true,
      withBestAddress: true,
      withBestEmail: true,
      withBestPhone: true,
      withEntityType: true
    });
  }

  setContactPhoneNumber(phone: PhoneDetailsFragment) {
    this.contactPhoneNumber = phone;
    this.selectedContactMethod = phone;
  }

  setContactEmail(email: EmailDetailsFragment) {
    this.contactEmail = email;
    this.selectedContactMethod = email;
  }

  getPhones(): PhoneDetailsFragment[] {
    return this.entity.phoneNumbers.filter(f => ![FieldStatus.Bad, FieldStatus.DoNotContact].includes(f.status));
  }

  getEmails(): EmailDetailsFragment[] {
    return this.entity.emails.filter(f => ![FieldStatus.Bad, FieldStatus.DoNotContact].includes(f.status));
  }

  addNewClicked(type: string) {
    this.openDialog(type)
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        switchMap((_) => this.fetchData().pipe(
          map((result) => result.data.getEntity)
        ))
      )
      .subscribe((entity) => {
        this.entity = entity;
        if (type === 'email') {
          this.setContactEmail(orderBy(entity.emails, ['updatedAt'], ['desc'])[0]);
        }
        if (type === 'phone') {
          this.setContactPhoneNumber(orderBy(entity.phoneNumbers, ['updatedAt'], ['desc'])[0]);
        }
        this._cd.detectChanges();
      });
  }


  private openDialog(type: string) {
    if (type === 'phone') {
      return AddOrUpdatePhoneDialog.open(this._dialog, {
        data: {
          entityId: this.entityId,
          campaignId: this.campaignId,
        }
      });
    }

    return AddOrUpdateEmailDialog.open(this._dialog, {
      data: {
        entityId: this.entityId,
        campaignId: this.campaignId,
      }
    });
  }

  isDoNotContact(type: IStatusable) {
    return type.status === FieldStatus.DoNotContact;
  }

  isBad(type: IStatusable) {
    return type.status === FieldStatus.Bad;
  }

  isVerified(type: IStatusable) {
    return type.status === FieldStatus.Verified;
  }

  saveClicked() {
    if (this.saving) {
      return;
    }

    this.saving = true;
    this._createContactAttemptGQL.mutate({
      input: this.getMutationInput(),
    }, {
      refetchQueries: [
        'GetEntityById2'
      ]
    }).pipe(
      finalize(() => {
        this.saving = false;
        this._cd.detectChanges();
      })
    ).subscribe((result) => {
      this._toastr.success('Conversation Logged');
      this.step = 2;
    });
  }

  logConversationClicked() {
    this.saveClicked();
  }

  logAttemptClicked() {
    // do some stuff, then close?
    this.closeModal.emit();
  }

  private getMutationInput(): CreateContactAttemptInput {
    const contactInfoData = this.getContactInfoData();
    return {
      entityId: this.entityId,
      targetId: this.customForm?.id,
      targetType: 'CustomForm',
      contactInfoType: contactInfoData.contactInfoType,
      contactInfoId: contactInfoData.contactInfoId,
      disposition: ContactAttemptDispositionType.Contacted,
      note: this.note
    };
  }

  private getContactInfoData(): {contactInfoType: ContactAttemptInfoType; contactInfoId: string} {
    if (this.selectedContactMethod.__typename === 'Email') {
      return {
        contactInfoType: ContactAttemptInfoType.Email,
        contactInfoId: this.selectedContactMethod.id
      };
    }
    if (this.selectedContactMethod.__typename === 'PhoneNumber') {
      return {
        contactInfoType: ContactAttemptInfoType.PhoneNumber,
        contactInfoId: this.selectedContactMethod.id
      };
    }

    return {
      contactInfoType: null,
      contactInfoId: null
    };
  }

}
